import React from "react"
import { ReactComponent as BuildingIcon } from "@ciq/shared/assets/icons/map/building_icon.svg"
import { ReactComponent as FloorIcon } from "@ciq/shared/assets/icons/map/floor_icon.svg"

/**
 * @typedef {Object} SidebarItem
 * @property {string} id - Unique identifier for the sidebar item
 * @property {string} label - Display label for the item
 * @property {string} [type] - Type of item: "inbox", "building", "floor"
 */

/**
 * Sidebar for the File Upload page showing Inbox and building/floor tree.
 * Supports drop targets for file-to-building drag-and-drop when handlers are provided.
 *
 * @param {Object} props
 * @param {string} props.selectedItemId - Currently selected sidebar item ID
 * @param {Function} props.onSelectItem - Callback when sidebar item is selected
 * @param {Array<SidebarItem>} props.items - Sidebar items to display
 * @param {Array<string>} [props.expandedBuildings] - IDs of expanded buildings
 * @param {Function} [props.onToggleBuilding] - Callback for building expand/collapse
 * @param {string|null} [props.activeDropTargetId] - ID of the building being hovered during drag
 * @param {Function} [props.onBuildingDragEnter] - Callback when drag enters a building
 * @param {Function} [props.onBuildingDragLeave] - Callback when drag leaves a building
 * @param {Function} [props.onBuildingDragOver] - Callback for dragOver (must preventDefault)
 * @param {Function} [props.onBuildingDrop] - Callback when files dropped on building
 * @returns {React.ReactElement} The sidebar component
 */
export function Sidebar({
  selectedItemId,
  onSelectItem,
  items,
  expandedBuildings = [],
  onToggleBuilding,
  activeDropTargetId = null,
  onBuildingDragEnter,
  onBuildingDragLeave,
  onBuildingDragOver,
  onBuildingDrop,
}) {
  const safeItems = items || []

  const renderPlacementIndicator = placementStatus => {
    switch (placementStatus) {
      case "placed":
        return (
          <span
            className="file-upload-placement-indicator indicator-placed"
            aria-label="Placed"
          >
            ✓
          </span>
        )
      case "not-placed":
        return (
          <span
            className="file-upload-placement-indicator indicator-not-placed"
            aria-label="Not placed"
          />
        )
      case "none":
      default:
        return (
          <span
            className="file-upload-placement-indicator indicator-none"
            aria-label="No upload"
          />
        )
    }
  }

  const renderedItems = safeItems.flatMap(item => {
    const hasFloors = item.type === "building" && Array.isArray(item.floors)
    const isExpanded = hasFloors && expandedBuildings.includes(item.id)
    const floors = isExpanded ? item.floors : []

    return [item, ...floors]
  })

  return (
    <div className="file-upload-sidebar">
      <div className="file-upload-sidebar-content" role="list" aria-label="File upload navigation">
        {renderedItems.map(item => {
          const isSelected = selectedItemId === item.id
          const hasFloors = item.type === "building" && Array.isArray(item.floors)
          const isExpanded = hasFloors && expandedBuildings.includes(item.id)
          const isFloor = item.type === "floor"
          const isDropTarget = item.type === "building" || item.type === "inbox"
          const isActiveTarget = activeDropTargetId === item.id

          const itemClassName = [
            "file-upload-sidebar-item",
            isSelected && "selected",
            isFloor && "floor-item",
            isDropTarget && isActiveTarget && "drop-target-active",
          ].filter(Boolean).join(" ")

          return (
          <div
            key={item.id}
            className={itemClassName}
            role="listitem"
            onDragEnter={isDropTarget && onBuildingDragEnter ? () => onBuildingDragEnter(item.id) : undefined}
            onDragLeave={isDropTarget && onBuildingDragLeave ? () => onBuildingDragLeave(item.id) : undefined}
            onDragOver={isDropTarget && onBuildingDragOver ? (e) => onBuildingDragOver(e) : undefined}
            onDrop={isDropTarget && onBuildingDrop ? (e) => onBuildingDrop(item.id, e) : undefined}
          >
            {hasFloors && (
              <button type="button"
                aria-label={`Toggle ${item.label}`}
                aria-expanded={isExpanded}
                onClick={() => onToggleBuilding && onToggleBuilding(item.id)}
              >
                {isExpanded ? "▾" : "▸"}
              </button>
            )}
            <button type="button"
              aria-pressed={isSelected}
              onClick={() => onSelectItem && onSelectItem(item.id)}
            >
              {item.type === "building" && (
                <BuildingIcon data-testid={`building-icon-${item.id}`} style={{ color: item.iconColor }} />
              )}
              {isFloor && (
                <FloorIcon data-testid={`floor-icon-${item.id}`} style={{ color: item.iconColor }} />
              )}
              <span>{item.label}</span>
              {item.type === "building" && item.fileCount > 0 && (
                <span className="file-upload-badge">{item.fileCount}</span>
              )}
              {isFloor && renderPlacementIndicator(item.placementStatus)}
            </button>
          </div>
          )
        })}
      </div>
    </div>
  )
}
